import type { Invitation } from "@/pages/admin/invitations/components/InvitationModal";
import type { SimpleClient, SimpleJob } from "../page";

const EVENT_TYPES = [
  "Wedding", "XV Años", "Baptism", "Graduation", "Birthday",
  "Baby Shower", "Engagement", "Anniversary", "Corporate Event", "Other",
];

interface Props {
  form: Invitation;
  clients: SimpleClient[];
  jobs: SimpleJob[];
  onChange: <K extends keyof Invitation>(key: K, val: Invitation[K]) => void;
}

export default function GeneralTab({ form, clients, jobs, onChange }: Props) {
  const handleTitleChange = (val: string) => {
    onChange("title", val);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-slate-800 font-semibold text-sm mb-1">Basic Information</h3>
        <p className="text-slate-400 text-xs">Link this invitation to a client and define the event type.</p>
      </div>

      {/* Client + Job */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Client <span className="text-red-400">*</span>
          </label>
          <select
            value={form.client_id}
            onChange={(e) => onChange("client_id", e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 appearance-none cursor-pointer transition-all"
          >
            <option value="">Select client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.full_name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Linked Job <span className="text-slate-300">(optional)</span>
          </label>
          <select
            value={form.job_id ?? ""}
            onChange={(e) => onChange("job_id", e.target.value || null)}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 appearance-none cursor-pointer transition-all"
          >
            <option value="">None</option>
            {jobs.map((j) => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Event type */}
      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Event Type <span className="text-red-400">*</span>
        </label>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {EVENT_TYPES.map((t) => (
            <button
              key={t}
              onClick={() => onChange("event_type", t)}
              className={`py-2 px-3 rounded-lg border text-xs font-medium transition-all cursor-pointer text-center whitespace-nowrap ${
                form.event_type === t
                  ? "border-indigo-400 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 text-slate-500 hover:border-slate-300 hover:text-slate-700 bg-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Internal title */}
      <div>
        <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
          Internal Title <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={form.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Ana & Carlos Wedding — internal reference"
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
        />
        <p className="text-slate-400 text-xs mt-1">Used only in the admin panel. Guests won&apos;t see this.</p>
      </div>

      {/* Event title (public) + Host names */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Event Title <span className="text-slate-300">(shown to guests)</span>
          </label>
          <input
            type="text"
            value={form.event_title ?? ""}
            onChange={(e) => onChange("event_title", e.target.value || null)}
            placeholder="Ana & Carlos"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
        </div>
        <div>
          <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
            Host Names
          </label>
          <input
            type="text"
            value={form.host_names ?? ""}
            onChange={(e) => onChange("host_names", e.target.value || null)}
            placeholder="Mr. & Mrs. García"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700 placeholder-slate-300 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
          />
        </div>
      </div>
    </div>
  );
}
